import { buildWitnessStepsForIndex } from '@/components/Event/NewScript/hooks/state/stateActions'
import { useStatePolicies } from '@/components/Event/NewScript/hooks/useStatePolicy'
import { useSaveScriptDataMutation } from '@/components/Event/NewScript/queries/scriptData'
import { isExaminationUnderOath } from '@/components/Event/NewScript/utils/event'
import {
  AttendeeRole,
  DepositionResult,
  ScriptStep,
  ScriptStepStatus,
} from '@/constants'
import { DRScriptData, Event } from '@/types'

export const useCreateInitialScriptState = ({ event }: { event?: Event }) => {
  const { mutateAsync: saveScriptData, isLoading } =
    useSaveScriptDataMutation(event)
  const { canHaveCna, shouldConfirmStipulations } = useStatePolicies(event)

  const initScriptData = () => {
    if (!event) {
      throw new Error('Cannot create initial script data without an event')
    }

    const payload = getInitialScriptData(
      event,
      canHaveCna,
      shouldConfirmStipulations,
    )

    // save the data to BE
    return saveScriptData(payload)
  }

  return { isLoading, initScriptData }
}

const getInitialScriptData = (
  event: Event,
  canHaveCna: boolean,
  shouldConfirmStipulations: boolean,
) => {
  const witnesses = event.attendees.filter(
    ({ role }) => role === AttendeeRole.WITNESS,
  )
  const witnessIds: Array<string | undefined> =
    witnesses.length > 0 ? witnesses.map(witness => witness.id) : [undefined]
  const attendees =
    event.attendees?.map(attendee => ({
      attendee_id: attendee.id,
      present: null,
      name: attendee.name,
      email: attendee.email || undefined,
      role: attendee.role,
      transcript_order: null,
      zoom_participants: [],
      identity_document: null,
    })) || []
  const witnessStepPolicies = {
    canHaveCna,
    isExaminationUnderOath: isExaminationUnderOath(event),
    shouldConfirmStipulations,
  }

  const reviewActions = witnessIds.reduce<
    Record<string, { key: string; completed: boolean; skip?: boolean }>
  >((acc, _, witnessIndex) => {
    acc[`review-${witnessIndex}`] = {
      key: `review-${witnessIndex}`,
      completed: false,
      skip: false,
    }

    return acc
  }, {})

  const payload: DRScriptData = {
    attendees,
    reporter_state: null,
    reporter_county: null,
    current_step: ScriptStep.PREPARATION,
    submitted: false,
    steps: {
      [ScriptStep.PREPARATION]: {
        key: ScriptStep.PREPARATION,
        status: ScriptStepStatus.IN_PROGRESS,
        actions: {
          join_zoom_using_meeting_id: {
            key: 'join_zoom_using_meeting_id',
            completed: false,
          },
          livestream_zoom_bot: {
            key: 'livestream_zoom_bot',
            completed: false,
            skip: event.is_zoombot_recording_enabled !== true,
          },
          create_breakout_rooms: {
            key: 'create_breakout_rooms',
            completed: false,
          },
          share_only_computer_audio: {
            key: 'share_only_computer_audio',
            completed: false,
          },
          open_live_transcript: {
            key: 'open_live_transcript',
            completed: false,
          },
          open_notice: {
            key: 'open_notice',
            completed: false,
          },
        },
      },
      ...witnessIds.reduce(
        (acc, _, witnessIndex) => ({
          ...acc,
          ...buildWitnessStepsForIndex(witnessIndex, witnessStepPolicies),
        }),
        {} as DRScriptData['steps'],
      ),
      [ScriptStep.REVIEW]: {
        key: ScriptStep.REVIEW,
        status: ScriptStepStatus.INCOMPLETE,
        actions: {
          ...reviewActions,
          low_confidence_words: {
            key: 'low_confidence_words',
            completed: false,
            skip: false,
          },
          location: {
            key: 'location',
            completed: false,
          },
        },
      },
    },
    events: witnessIds.map(witnessId => ({
      current_step: null,
      first_skipped_step: null,

      // Attendance step
      attendees: attendees.map(attendee => ({
        ...attendee,
        role:
          attendee.role === AttendeeRole.WITNESS &&
          witnessId !== undefined &&
          witnessId !== attendee.attendee_id
            ? AttendeeRole.OTHER
            : attendee.role,
      })),
      zoom_participants: [],
      low_confidence_words: null,

      // Opening
      isRecordingStarted: undefined,

      // Questioning step
      last_timestamp: null,
      is_on_record: false,

      timestamps: null,
      spellings: null,
      notes: null,

      // Closing default
      witness_requested_transcript: null,
      read_waive_answer: 'no_answer' as const,
      errata_recipient_attendee_id: null,
      order_transcript: false,
      isRecordingEnded: undefined,
      cna_declared_at: null,

      // Closing event canceled
      reason_for_cna: undefined,
      other_reason: '',

      // Review
      submitted: false,
      shown_exhibits: null, // TODO: deprecated, keeping for backward compatibility
      exhibits: null,
      confirmed_no_exhibits: null,

      // Event result
      result: null as never as DepositionResult, // FIXME: This workaround is used to avoid Yup typing issues
    })),
  }

  return payload
}
